import React, { useMemo } from 'react'

import { TrashIcon } from '@heroicons/react/24/outline'
import { ErrorMessage, Field, FieldArray, Form, Formik, FormikValues } from 'formik'
import _startCase from 'lodash/startCase'
import ReactSelect from 'react-select'
import * as yup from 'yup'

import { useFileManager } from '../useFileManager'
import { useAppDispatch } from '@/client/hooks'
import { MAX_FILE_SIZE, setFileNotifications } from '@/features/FileManager/fileManagerSlice'
import { formatBytes } from '@/utils'

interface FilesRenameForm {
  filesToUpload: File[]
  handleFilesRename: (values: FormikValues) => void
  handleFileRemoval: (index: number) => void
  isUploading: boolean
  forcedTag?: string
}

export function FilesRenameForm({
  filesToUpload,
  handleFilesRename,
  handleFileRemoval,
  isUploading,
  forcedTag = '',
}: FilesRenameForm) {
  const { documentTemplatesLoaded, fileRenameRegex, calculateSuggestedGroupedOptions } =
    useFileManager()

  const initialValues = {
    filesToUpload,
    renamedFiles: filesToUpload.map(() => ({
      uuid: '',
      category: '',
      name: '',
      identifier: '',
      date: new Date().toISOString().split('T')[0],
      newName: '',
      tag: forcedTag,
      kind: '',
    })),
  }
  const dispatch = useAppDispatch()

  const validationSchema = yup.object().shape({
    filesToUpload: yup
      .array()
      .of(
        yup
          .mixed()
          .test(
            'fileSize',
            `File too large (max ${formatBytes(MAX_FILE_SIZE)})`,
            (value) => value && value.size <= MAX_FILE_SIZE
          )
      ),
    renamedFiles: yup.array().of(
      yup.object().shape({
        name: yup.string().required('*').max(254, 'Max 254 characters'),
        date: yup.string().required('*'),
        identifier: yup
          .string()
          .max(254, 'Max 254 characters')
          .matches(fileRenameRegex, 'Cannot start or end with whitespace'),
      })
    ),
  })

  const groupedOptions = useMemo(
    () => calculateSuggestedGroupedOptions(filesToUpload),
    [documentTemplatesLoaded]
  )

  const submitHandler = (values: FormikValues) => {
    const tags = values.renamedFiles.map((file) => file.tag)
    dispatch(setFileNotifications(tags))
    handleFilesRename(values)
  }

  return (
    <>
      {documentTemplatesLoaded && (
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={submitHandler}
        >
          {({ values, setValues }) => (
            <Form>
              <fieldset>
                <table className='w-full mt-5 border-separate border-spacing-3'>
                  <thead>
                    <tr className='text-left text-sm'>
                      <th className='font-light'>Original Document Name</th>
                      <th className='font-light'>Document *</th>
                      <th className='font-light'>Target Folder</th>
                      <th className='font-light'>Identifier (optional)</th>
                      <th className='font-light'>Date *</th>
                      <th>&nbsp;</th>
                    </tr>
                  </thead>
                  <tbody>
                    <FieldArray name='filesToUpload'>
                      {({ remove }) =>
                        values?.filesToUpload?.map((field, index) => (
                          <tr key={index}>
                            <td className='font-semibold overflow-hidden text-ellipsis whitespace-nowrap'>
                              {field.name}
                              <ErrorMessage
                                name={`filesToUpload.${index}`}
                                render={(msg) => <div className='text-red-600'> {msg}</div>}
                              />
                            </td>
                            <td className='w-96'>
                              <div className='flex gap-3 items-center'>
                                <Field
                                  name={`renamedFiles.${index}.name`}
                                  className='w-full'
                                  component={ReactSelect}
                                  isClearable
                                  options={groupedOptions[index]}
                                  defaultValue={() => {
                                    const suggestedOption = groupedOptions[index][0]?.options[0]
                                    values.renamedFiles[index].name = suggestedOption?.label ?? ''
                                    values.renamedFiles[index].tag =
                                      forcedTag || (suggestedOption?.tag ?? '')
                                    values.renamedFiles[index].kind = suggestedOption?.kind ?? ''
                                    values.renamedFiles[index].category = _startCase(
                                      suggestedOption?.category ?? ''
                                    )
                                    setValues(values)
                                    return groupedOptions[index][0]?.options[0]
                                  }}
                                  onChange={(doc) => {
                                    const updatedFiles = [...values.renamedFiles]
                                    updatedFiles[index] = {
                                      ...updatedFiles[index],
                                      name: doc?.label ?? '',
                                      tag: forcedTag || (doc?.tag ?? ''),
                                      kind: doc?.kind ?? '',
                                      category: _startCase(doc?.category ?? ''),
                                    }
                                    setValues({ ...values, renamedFiles: updatedFiles })
                                  }}
                                  classNames={{ menuList: () => 'h-32' }}
                                />
                                <ErrorMessage
                                  name={`renamedFiles.${index}.name`}
                                  render={(msg) => (
                                    <div className='text-red-600 text-lg'>{msg}</div>
                                  )}
                                />
                              </div>
                            </td>
                            <td className='form-input border-gray-300 rounded-md h-[38px] text-gray-500 text-sm'>
                              {values.renamedFiles[index].name
                                ? values.renamedFiles[index].tag || 'Current folder'
                                : ''}
                            </td>
                            <td className='w-36'>
                              <Field
                                name={`renamedFiles.${index}.identifier`}
                                className='form-input border-gray-300 rounded-md h-[38px] w-full text-gray-600 text-sm'
                              />
                              <ErrorMessage
                                name={`renamedFiles.${index}.identifier`}
                                render={(msg) => (
                                  <div className='text-red-600 flex-inline w-36'>{msg}</div>
                                )}
                              />
                            </td>
                            <td className='w-36'>
                              <div className='flex gap-3 items-center'>
                                <Field
                                  name={`renamedFiles.${index}.date`}
                                  className='border-gray-300 rounded-md h-[38px] w-full text-gray-600 text-sm'
                                  type='date'
                                />
                                <ErrorMessage
                                  name={`renamedFiles.${index}.date`}
                                  render={(msg) => (
                                    <div className='text-red-600 text-lg'>{msg}</div>
                                  )}
                                />
                              </div>
                            </td>
                            <td>
                              <TrashIcon
                                width={20}
                                className='cursor-pointer stroke-1'
                                onClick={() => {
                                  remove(index)

                                  // Imperatively remove the item in renamedFiles
                                  // for validation to work properly
                                  values.renamedFiles.splice(index, 1)

                                  // Remove File from State
                                  handleFileRemoval(index)
                                }}
                              />
                            </td>
                          </tr>
                        ))
                      }
                    </FieldArray>
                  </tbody>
                </table>
              </fieldset>
              <div className='flex justify-end mr-12 my-3'>
                <button
                  type='submit'
                  className='bg-blue-500 text-white font-bold py-2 px-5 rounded disabled:opacity-50'
                  disabled={isUploading}
                >
                  Rename Files and Upload
                </button>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </>
  )
}
